import cn from 'classnames';

import { CustomPlotStylingOptions } from '@components/analysisCategories/comparative/plots/PlotlyVolcanoPlotUtil';
import LoadingMessage from '@components/LoadingMessage';
import ExperimentPlotView, { LegendPosition, Props as PlotProps } from '@components/plots/PlotView';

import { useOptionalExperimentDetailViewContext } from '@contexts/ExperimentDetailViewContext';

import usePlot from '@hooks/usePlot';

import Experiment from '@models/Experiment';

import CommentsModal from '../experiments/comments/CommentsModal';

type Props = {
    experiment: Experiment;
    plotId: string;
    className?: string;
    legendPosition?: LegendPosition;
    disableFullWidth?: boolean;
    publicationMode?: boolean;
    hideLegend?: boolean;
    aspectRatio?: number | null;
    hideMoreMenu?: boolean;
    publicKey?: string | null;
    customPlotStylingOptions?: CustomPlotStylingOptions | null;
    xAxisTruncated?: boolean;
} & Partial<PlotProps>;

const EmbeddedPlotView = ({
    experiment,
    plotId,
    className,
    legendPosition,
    disableFullWidth,
    publicationMode,
    hideLegend = false,
    hideMoreMenu = false,
    showExperimentLink = true,
    overrides,
    publicKey,
    customPlotStylingOptions,
    xAxisTruncated = false,
    ...plotProps
}: Props) => {
    const { plot, plotLoading } = usePlot({ experiment, plotId, overrides, publicKey });
    const { commentsModalOpen, setCommentsModalOpen } = useOptionalExperimentDetailViewContext();

    const loading = plotLoading;

    return (
        <>
            <div className={cn(className)}>
                {loading && (
                    <div className="py-8">
                        <LoadingMessage message="Plot loading" />
                    </div>
                )}
                {plot && !loading && (
                    <>
                        <ExperimentPlotView
                            {...plotProps}
                            overrides={overrides}
                            experiment={experiment}
                            plot={plot}
                            hideEditButton
                            legendPosition={legendPosition}
                            showExperimentLink={showExperimentLink}
                            hideLegend={hideLegend}
                            hideMoreMenu={hideMoreMenu}
                            disableFullWidth={disableFullWidth}
                            disableRoundedCorners
                            publicKey={publicKey}
                            publicationMode={publicationMode}
                            customPlotStylingOptions={customPlotStylingOptions}
                            xAxisTruncated={xAxisTruncated}
                            disablePlotImages
                        />
                        <CommentsModal
                            open={!!commentsModalOpen}
                            plot={plot}
                            onClose={() => setCommentsModalOpen?.(false)}
                        />
                    </>
                )}
            </div>
        </>
    );
};

export default EmbeddedPlotView;
